import { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { safeGet, safeSet, safeRemove } from '@/shared/lib/storage';
import { updateWorkLog } from '../api';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'retrying' | 'failed';

const LS_KEY_PREFIX = 'worklog-autosave-';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const LocalBackupSchema = z.object({
  content: z.string(),
  savedAt: z.string(),
});
type LocalBackup = z.infer<typeof LocalBackupSchema>;

function backupKey(workLogId: string): string {
  return `${LS_KEY_PREFIX}${workLogId}`;
}

function getLocalBackup(workLogId: string): LocalBackup | null {
  const parsed = safeGet(backupKey(workLogId), LocalBackupSchema.nullable(), null);
  if (!parsed) {
    safeRemove(backupKey(workLogId));
    return null;
  }
  return parsed;
}

function setLocalBackup(workLogId: string, content: string): void {
  safeSet(backupKey(workLogId), { content, savedAt: new Date().toISOString() });
}

function clearLocalBackup(workLogId: string): void {
  safeRemove(backupKey(workLogId));
}

async function saveWithRetry(
  workLogId: string,
  content: string,
  onStatusChange: (status: SaveStatus) => void
): Promise<boolean> {
  setLocalBackup(workLogId, content);

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      onStatusChange(attempt === 0 ? 'saving' : 'retrying');
      await updateWorkLog(workLogId, content);
      clearLocalBackup(workLogId);
      onStatusChange('saved');
      return true;
    } catch {
      if (attempt < MAX_RETRIES - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY));
      }
    }
  }

  onStatusChange('failed');
  return false;
}

export function useDebouncedAutoSave(
  workLogId: string,
  content: string,
  status: 'DRAFT' | 'SUBMITTED',
  serverUpdatedAt: string,
  delay = 2000
) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingContentRef = useRef<string | null>(null);
  const lastSavedContentRef = useRef(content);
  const isMountedRef = useRef(true);
  const isSavingRef = useRef(false);

  // Recovery check on mount
  const [recoveredContent] = useState<string | null>(() => {
    const backup = getLocalBackup(workLogId);
    if (!backup) return null;
    if (new Date(backup.savedAt) > new Date(serverUpdatedAt)) {
      return backup.content;
    }
    clearLocalBackup(workLogId);
    return null;
  });

  const doSave = useCallback(
    async (contentToSave: string) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      await saveWithRetry(workLogId, contentToSave, (s) => {
        if (isMountedRef.current) setSaveStatus(s);
      });
      isSavingRef.current = false;
      lastSavedContentRef.current = contentToSave;
    },
    [workLogId]
  );

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingContentRef.current;
    if (pending !== null && pending !== lastSavedContentRef.current) {
      pendingContentRef.current = null;
      await doSave(pending);
    }
  }, [doSave]);

  // Debounced auto-save on content change — allowed for both DRAFT and SUBMITTED
  // (server-side updateWorkLog increments revisionCount when SUBMITTED).
  useEffect(() => {
    if (content === lastSavedContentRef.current) return;

    pendingContentRef.current = content;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const c = pendingContentRef.current;
      if (c !== null && c !== lastSavedContentRef.current) {
        pendingContentRef.current = null;
        void doSave(c);
      }
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [content, status, delay, doSave]);

  // Flush on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const pending = pendingContentRef.current;
      if (pending !== null && pending !== lastSavedContentRef.current) {
        void saveWithRetry(workLogId, pending, () => {});
      }
    };
  }, [workLogId]);

  return { saveStatus, flush, recoveredContent };
}
